/**
 * EVENTSFLO-USER-GUIDE-V1
 *
 * Reusable "Guide" button — drop on any page header. Opens a slide-out
 * panel that fetches /api/user-guide and renders the markdown.
 *
 * Usage:
 *   <GuideButton />                    // small icon+label button
 *   <GuideButton variant="ghost" />    // override style
 *   <GuideButton section="dashboard"/> // future: anchor to a heading
 *
 * The component auto-fetches the markdown the first time it opens and
 * caches it in component state so reopens are instant.
 */
import { useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { BookOpen, X, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = "eventsflo_auth_token";
const CLIENT_TOKEN_KEY = "eventsflo_client_token";

function readAuthHeader() {
  const adminTok =
    typeof window !== "undefined"
      ? localStorage.getItem(ADMIN_TOKEN_KEY)
      : null;
  if (adminTok) return { Authorization: `Bearer ${adminTok}` };
  const clientTok =
    typeof window !== "undefined"
      ? localStorage.getItem(CLIENT_TOKEN_KEY)
      : null;
  if (clientTok) return { Authorization: `Bearer ${clientTok}` };
  return {};
}

export function GuideButton({
  label = "Guide",
  variant = "outline",
  size = "sm",
  className = "",
  testId = "guide-button",
}) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchGuide = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/user-guide`, {
        headers: { ...readAuthHeader() },
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      setMarkdown(body.markdown || "");
      setUpdatedAt(body.updated_at || null);
      setLoaded(true);
    } catch (e) {
      setError(e.message || "Failed to load guide");
    } finally {
      setLoading(false);
    }
  }, []);

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && !loaded && !loading) {
      fetchGuide();
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={`gap-1.5 ${className}`}
        onClick={() => onOpenChange(true)}
        data-testid={testId}
      >
        <BookOpen className="h-4 w-4" />
        {label}
      </Button>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="guide-sheet"
        >
          <SheetHeader className="text-left pr-8">
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              EventsFlo User Guide
            </SheetTitle>
            <SheetDescription className="text-xs">
              First-pass walkthrough of the admin, client and crew
              portals. Updates roll out without a deploy — refresh to
              see the latest copy.
              {updatedAt && (
                <span className="block mt-1 font-mono text-[10px] opacity-70">
                  Last updated: {new Date(updatedAt).toLocaleString()}
                </span>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            {loading && (
              <div
                className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"
                data-testid="guide-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin" /> Loading guide…
              </div>
            )}

            {error && !loading && (
              <div
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-3"
                data-testid="guide-error"
              >
                Couldn't load the guide: {error}
                <button
                  type="button"
                  className="ml-2 underline"
                  onClick={fetchGuide}
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && markdown && (
              <div
                className="prose prose-invert prose-sm max-w-none
                           prose-headings:font-secondary prose-headings:tracking-tight
                           prose-h1:text-2xl prose-h1:mt-0
                           prose-h2:text-xl prose-h2:mt-8
                           prose-h3:text-base prose-h3:mt-6
                           prose-p:text-sm prose-li:text-sm
                           prose-code:text-[12px] prose-code:bg-muted/40
                           prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                           prose-table:text-xs
                           prose-a:text-primary"
                data-testid="guide-markdown"
              >
                <ReactMarkdown>{markdown}</ReactMarkdown>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 p-1 rounded hover:bg-muted/40"
            aria-label="Close guide"
            data-testid="guide-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default GuideButton;
